import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import InfoSvg from "../../../../common/svgs/InfoSvg";
import RecordTable, { RecordColumn } from "../common/RecordTable";
import { formatDisplayDate } from "../../../../common/utils/date";
import { usePnoeCatalogs } from "../orders/usePnoeMeasurements";
import {
  getStatusBadgeStyle,
  getStatusIconColor,
} from "../../utils/labPanelStatus";
import useAssessmentHistory from "./useAssessmentHistory";
import HistoryBanner from "./HistoryBanner";
import useHistoryScope from "./useHistoryScope";
import { useHistoryToolbarSlots } from "./HistoryToolbar";
import { AssessmentHistoryRow } from "./assessmentHistory";
import { historyLoadingRestText } from "./useHistoryList";

/**
 * History → Assessments: lab panels, PNOĒ breath tests and InBody tests in one
 * table, told apart by the Type column (design nodes 16662:113983 for the
 * affiliate, 16579:105118 for the customer).
 *
 * Replaces the two-tab Orders screen. The columns are the same in every scope
 * — Date, Type, Detail, Provider, Device, Status — with a Client column added
 * on the cross-client page only.
 */
const AssessmentsHistoryTab = () => {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const scope = useHistoryScope();
  const catalogs = usePnoeCatalogs();
  const toolbarSlots = useHistoryToolbarSlots();
  const {
    rows,
    loading,
    labError,
    pnoeError,
    truncated,
    inbodyError,
    listsLoadingRest,
    listsLoaded,
    listsTotal,
  } = useAssessmentHistory(scope, catalogs);

  const showClient = !scope.isSingleSubject;

  const columns = useMemo<RecordColumn<AssessmentHistoryRow>[]>(() => {
    const dateColumn: RecordColumn<AssessmentHistoryRow> = {
      key: "date",
      label: "Date",
      // A date is always the same width ("Aug 28 2026"); the applied range
      // in the filter header wraps rather than claiming more room.
      flex: 1.2,
      filter: "date",
      date: (row) => row.date,
      value: (row) => (row.date ? formatDisplayDate(row.date) : "—"),
    };
    const typeColumn: RecordColumn<AssessmentHistoryRow> = {
      key: "type",
      label: "Type",
      flex: 1.5,
      value: (row) => row.typeLabel,
    };
    const providerColumn: RecordColumn<AssessmentHistoryRow> = {
      key: "provider",
      label: "Provider",
      flex: 1.4,
      value: (row) => row.provider,
    };
    const statusColumn: RecordColumn<AssessmentHistoryRow> = {
      key: "status",
      label: "Status",
      flex: 2,
      optionKey: (row) => row.statusKey,
      value: (row) => row.statusLabel,
      render: (row) =>
        // A status this build doesn't recognise has no label; render no badge
        // rather than an empty one, as the Orders table did.
        row.statusLabel ? (
          <div style={getStatusBadgeStyle(row.statusTone, theme)}>
            {row.actionRequired &&
              (row.statusTone === "warning" ||
                row.statusTone === "danger") && (
                <InfoSvg
                  color={getStatusIconColor(row.statusTone, theme)}
                  size={12}
                />
              )}
            {row.statusLabel}
          </div>
        ) : null,
    };

    // One column set for every scope; the single-subject views only drop
    // Client, since the whole page is about that one person.
    return [
      dateColumn,
      ...(showClient
        ? [
            {
              key: "client",
              label: "Client",
              flex: 1.8,
              searchable: true,
              withInitials: true,
              optionKey: (row) => row.clientId ?? row.clientName,
              value: (row) => row.clientName,
            } as RecordColumn<AssessmentHistoryRow>,
          ]
        : []),
      // Type before Detail: the kind of assessment is what the reader scans
      // for, and it decides how to read the Detail beside it.
      typeColumn,
      { key: "detail", label: "Detail", flex: 1.6, value: (row) => row.detail },
      providerColumn,
      // The widest free-text cell ("PNOĒ 1 · PNOE 2316-135"), so it takes the
      // room the Date column gives up.
      { key: "device", label: "Device", flex: 2.2, value: (row) => row.device },
      statusColumn,
    ];
  }, [showClient, theme]);

  const banners = (
    <>
      {inbodyError && (
        <HistoryBanner tone="warning" text={`${inbodyError} InBody tests are not included below.`} />
      )}
      {listsLoadingRest && (
        <HistoryBanner tone="info" text={historyLoadingRestText(listsLoaded, listsTotal)} />
      )}
      {labError && (
        <HistoryBanner tone="danger" text={labError} />
      )}
      {pnoeError && (
        <HistoryBanner tone="warning" text={`${pnoeError} PNOĒ tests are not included below.`} />
      )}
      {truncated && (
        <HistoryBanner tone="info" text="Only the most recent PNOĒ tests are shown." />
      )}
    </>
  );

  return (
    <RecordTable<AssessmentHistoryRow>
      columns={columns}
      rows={rows}
      rowKey={(row) => `${row.kind}:${row.id}`}
      loading={loading}
      banners={banners}
      clearFiltersSlot={toolbarSlots.filters}
      // Remembered per scope as well as per tab: the cross-client view's
      // Client filter must not follow the user into a single-client
      // workspace, where that column does not exist.
      persistKey={`history:${scope.kind}:assessments`}
      emptyMessage="No assessments found"
      filteredEmptyMessage="No assessments match these filters"
      accent={(row) =>
        row.actionRequired
          ? row.statusTone === "danger"
            ? theme.colorTokens.D[70]
            : theme.colorTokens.W[70]
          : null
      }
      reviewTone={(row) => (row.actionRequired ? row.statusTone : null)}
      onRowClick={(row) => {
        // Detail routes are nested under this tab's segment, so the tab stays
        // active while a record is open. `location.search` carries `?id=`.
        const segment =
          row.kind === "pnoe" ? "pnoe" : row.kind === "inbody" ? "inbody" : "lab";
        navigate(
          `${scope.basePath}/assessments/${segment}/${row.id}${location.search}`,
          // The InBody biomarkers endpoint returns no record fields (device,
          // dates); the listing row has them, so it travels along. A cold
          // load falls back to looking the record up.
          { state: row.kind === "inbody" ? { record: row.raw } : undefined }
        );
      }}
    />
  );
};

export default AssessmentsHistoryTab;
